import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BdsIcon, BdsTooltip } from '@/core/constants/webComponents';
import { REFRESH_SUPPORTED_PLATFORMS } from '@/core/constants/platforms';
import { CARD_POST_EVENTS, CardPostEventName } from '@/features/publication-wall/constants/events';
import { FooterMetaModal } from './FooterMetaModal';

interface PostFooterMetaProps {
  updatedAt: string;
  groups: Array<{ name: string; count?: number }>;
  themes: Array<{ name: string; count?: number }>;
  interest?: Array<{ name: string }>;
  postId?: string;
  uid?: string;
  channel?: string;
  platform?: string;
  isInteraction?: boolean;
  eventName?: CardPostEventName;
  post_id?: string;
  sac_call_id?: string;
}

export const PostFooterMeta: React.FC<PostFooterMetaProps> = ({
  updatedAt,
  groups,
  themes,
  interest,
  postId,
  uid,
  channel,
  platform = '',
  isInteraction = false,
  eventName = CARD_POST_EVENTS.FOOTER_META_CLICK,
  post_id,
  sac_call_id,
}) => {
  const { t } = useTranslation();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (refreshTimerRef.current !== null) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);
  const formatMetaItem = (name: string, count?: number) => (count ? `${name} (+${count})` : name);

  const groupsLabel = t('cardPosts.group').replaceAll(':', '');
  const themesLabel = t('cardPosts.theme').replaceAll(':', '');
  const interestLabel = t('cardPosts.interest').replaceAll(':', '');

  const dispatchMetaClick = (
    section: 'updatedAt' | 'refresh' | 'group' | 'theme' | 'interest',
    value: string,
    index?: number,
  ) => {
    document.dispatchEvent(
      new CustomEvent(eventName, {
        detail: {
          postId,
          section,
          value,
          index,
        },
        bubbles: true,
        composed: true,
      }),
    );
  };

  const renderMetaItems = (
    items: Array<{ name: string; count?: number }> | undefined,
    section: 'group' | 'theme' | 'interest',
    label: string,
    limit: number,
  ) => {
    if (!items || items.length === 0) {
      return null;
    }

    const visibleItems = items.slice(0, limit);
    const remainingCount = items.length - visibleItems.length;

    return (
      <>
        <button
          type="button"
          className="post-card__meta-label post-card__meta-item-btn"
          onClick={() => setShowModal(true)}
          aria-label={t('cardPosts.allFooterMetaTitle')}
        >
          {label}:
        </button>
        <span className="post-card__meta-list">
          {visibleItems.map((item, index) => {
            const text = formatMetaItem(item.name, item.count);

            return (
              <React.Fragment key={`${section}-${item.name}-${index}`}>
                <BdsTooltip position="top-center" tooltip-text={text}>
                  <button
                    type="button"
                    className="post-card__meta-item-btn post-card__meta-bold"
                    onClick={() => dispatchMetaClick(section, text, index)}
                  >
                    {text}
                  </button>
                </BdsTooltip>
                {index < visibleItems.length - 1 && <span>, </span>}
              </React.Fragment>
            );
          })}
          {remainingCount > 0 && <span>&nbsp;(+{remainingCount})</span>}
        </span>
      </>
    );
  };

  return (
    <>
    <div className="post-card__footer-meta">
      <div className="post-card__updated">
        <BdsTooltip position="top-center" tooltip-text={`${t('cardPosts.updatedAt')} ${updatedAt}`}>
          <button
            type="button"
            className="post-card__meta-item-btn"
            onClick={() => dispatchMetaClick('updatedAt', updatedAt)}
          >
            {t('cardPosts.updatedAt')} {updatedAt}
          </button>
        </BdsTooltip>
        {REFRESH_SUPPORTED_PLATFORMS.has(platform) && !isInteraction && (
          <span className="post-card__refresh">
            <button
              type="button"
              className={`post-card__refresh-btn${isRefreshing ? ' post-card__refresh-btn--loading' : ''}`}
              title={t('cardPosts.refreshData')}
              aria-label={t('cardPosts.refreshData')}
              disabled={isRefreshing}
              onClick={() => {
                if (isRefreshing) return;
                setIsRefreshing(true);
                dispatchMetaClick('refresh', updatedAt);
                document.dispatchEvent(
                  new CustomEvent(CARD_POST_EVENTS.REFRESH_DATA, {
                    detail: { postId, channel, uid, post_id, sac_call_id },
                    bubbles: true,
                    composed: true,
                  }),
                );
                if (refreshTimerRef.current !== null) {
                  clearTimeout(refreshTimerRef.current);
                }
                refreshTimerRef.current = setTimeout(() => {
                  refreshTimerRef.current = null;
                  setIsRefreshing(false);
                }, 5000);
              }}
            >
              <BdsIcon theme="outline" name="refresh" size="small" />
            </button>
          </span>
        )}
      </div>
      <div className="post-card__meta-row">
        <div className="post-card__groups-themes">
          {renderMetaItems(groups, 'group', groupsLabel, 2)}
          {themes?.length > 0 && groups?.length > 0 && (
            <span className="post-card__meta-separator">&nbsp;•&nbsp;</span>
          )}
          {renderMetaItems(themes, 'theme', themesLabel, 2)}
        </div>
      </div>
      <div className="post-card__meta-row">
        <div className="post-card__interest">
          {interest && interest.length > 0 && renderMetaItems(interest, 'interest', interestLabel, 2)}
        </div>
      </div>
    </div>

    {showModal && (
      <FooterMetaModal
        groups={groups}
        themes={themes}
        interest={interest}
        postId={postId}
        eventName={eventName}
        onClose={() => setShowModal(false)}
      />
    )}
    </>
  );
};
